/** Status a client organization can be in. */
export type OrganizationStatus = 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';

/**
 * Authority over a client organization as a whole.
 *
 * Only two values: "team admin" is not an organization-level fact, it is
 * something someone is *of a particular team*, and it lives on their team
 * membership instead.
 */
export type OrgRole = 'ORG_ADMIN' | 'MEMBER';

/** Authority within one team. */
export type TeamRole = 'TEAM_ADMIN' | 'MEMBER';

/**
 * Human labels, kept beside the type so a new role cannot ship showing its raw
 * enum name in the UI.
 */
export const ORG_ROLE_LABELS = {
  ORG_ADMIN: 'Organization admin',
  MEMBER: 'Member',
} as const satisfies Record<OrgRole, string>;

/** What each organization role may do, shown when assigning one. */
export const ORG_ROLE_DESCRIPTIONS = {
  ORG_ADMIN:
    'Manages the agreement, shared credits, teams, every member, approvals and reporting.',
  MEMBER: 'Requests programs. What else they can do comes from the teams they are in.',
} as const satisfies Record<OrgRole, string>;

/** The same roles on a showcase partner, in the partner portal's terms. */
export const PARTNER_ROLE_DESCRIPTIONS = {
  ORG_ADMIN: 'Manages the showcase, city page card, photos, settings and their team.',
  MEMBER: 'Sees the showcase, its leads and performance, and can edit what an admin allows.',
} as const satisfies Record<OrgRole, string>;

/** Labels for team-level authority. */
export const TEAM_ROLE_LABELS = {
  TEAM_ADMIN: 'Team admin',
  MEMBER: 'Member',
} as const satisfies Record<TeamRole, string>;

/** An organization as the list endpoint returns it. */
export interface OrganizationSummary {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly customDomain: string | null;
  readonly status: OrganizationStatus;
  readonly createdAt: string;
  readonly memberCount: number;
  readonly portalUrl: string;
  readonly brand: { readonly logoUrl: string | null; readonly primaryColor: string } | null;
  /** A client's portal or a showcase partner (older responses omit it: a client). */
  readonly kind?: OrganizationKind;
  /** A fictional client kept for demos, left out of internal lists and reports (older responses omit it: real). */
  readonly isDemo?: boolean;
}

export const ORGANIZATION_KINDS = { client: 'CLIENT', partner: 'PARTNER' } as const;
export type OrganizationKind = (typeof ORGANIZATION_KINDS)[keyof typeof ORGANIZATION_KINDS];

/** Where a partner's placement check stands (the API's PLACEMENT_STATUS). Only `verified` publishes their card. */
export const PLACEMENT_STATUS = { none: 'none', verified: 'verified', missing: 'missing', unreachable: 'unreachable' } as const;
export type PlacementStatus = (typeof PLACEMENT_STATUS)[keyof typeof PLACEMENT_STATUS];

/** One partner on GET /organizations/partner-placements: whether they have put us on their site. */
export interface PartnerPlacement {
  readonly organizationId: string;
  readonly name: string;
  readonly slug: string;
  readonly status: PlacementStatus;
  /** The page we check, as the partner gave it. */
  readonly url: string | null;
  readonly checkedAt: string | null;
  readonly verifiedAt: string | null;
  /** City page slugs their card is set for. */
  readonly cities: readonly string[];
}

export const PARTNER_TYPE_LABELS = {
  HOTEL: 'Hotel or resort',
  VENUE: 'Event venue',
  CHARITY: 'Nonprofit',
} as const;
export type PartnerType = keyof typeof PARTNER_TYPE_LABELS;

/** A partner's showcase as staff see it on the partner's page. */
export interface PartnerSummary {
  readonly partnerType: PartnerType;
  readonly showcaseUrl: string;
  readonly programCount: number;
  readonly featuredCount: number;
  readonly notifyPartner: boolean;
  readonly allowedOrigins: readonly string[];
}

/** A client's branding record. */
export interface Brand {
  readonly id: string;
  readonly organizationId: string;
  readonly logoUrl: string | null;
  /** Logo variant for dark surfaces (light artwork), e.g. the sidebar. */
  readonly logoDarkUrl?: string | null;
  readonly primaryColor: string;
  readonly sidebarColor: string;
  readonly accentColor: string;
  readonly fontFamily: string;
  readonly heroImageUrl: string | null;
  readonly heroQuote: string | null;
  readonly heroStatValue: string | null;
  readonly heroStatLabel: string | null;
  readonly loginEyebrow: string | null;
  readonly loginHeadline: string | null;
  readonly loginSubcopy: string | null;
  readonly welcomeMessage: string | null;
}

/** An organization as the detail endpoint returns it. */
export interface OrganizationDetail extends Omit<OrganizationSummary, 'brand' | 'memberCount'> {
  readonly clerkOrgId: string;
  readonly brand: Brand | null;
  readonly teams: readonly { readonly id: string; readonly name: string }[];
  readonly _count: { readonly memberships: number; readonly bookingRequests: number };
  /** Salesforce accounts linked one by one (the organization ↔ event join). */
  readonly salesforceAccountIds: readonly string[];
  /** Salesforce accounts whose child accounts roll in as well. */
  readonly salesforceParentAccountIds: readonly string[];
  /** Set for a partner; null while its showcase is not set up. */
  readonly partner?: PartnerSummary | null;
}

/** A Salesforce account as the picker's search returns it. */
export interface SalesforceAccountHit {
  readonly id: string;
  readonly name: string;
  readonly type: string | null;
  /** "City, ST" from the billing address, when Salesforce has one. */
  readonly billing: string | null;
  readonly parent: { readonly id: string; readonly name: string } | null;
}

/** A Salesforce account linked to an organization. */
export interface LinkedSalesforceAccount extends SalesforceAccountHit {
  /** Linked as a parent: its child accounts roll in too. */
  readonly includesChildren: boolean;
  readonly children: readonly { readonly id: string; readonly name: string }[];
}

/** What the linked-accounts endpoint returns. */
export interface LinkedSalesforceAccounts {
  readonly accounts: readonly LinkedSalesforceAccount[];
  /** False when Salesforce was down, so names and children are missing. */
  readonly salesforceReachable: boolean;
}

/** A membership row — the client's member roster. */
export interface Member {
  readonly id: string;
  readonly email: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly title: string | null;
  readonly role: OrgRole;
  /** Teams they are in, with their role in each. */
  readonly teams: readonly {
    readonly id: string;
    readonly name: string;
    readonly role: TeamRole;
  }[];
  readonly creditApprovalLimit: number | null;
  readonly eventSizeLimit: number | null;
  readonly isActive: boolean;
  readonly isPending: boolean;
  readonly department: { readonly id: string; readonly name: string } | null;
  readonly region: { readonly id: string; readonly name: string } | null;
}

/** Result of the live slug availability check. */
export interface SlugCheck {
  readonly slug: string;
  readonly available: boolean;
  readonly reason?: string;
  readonly message?: string;
  readonly portalUrl?: string;
}
